import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Spinner } from "reactstrap"
import { EngDiagQuestionView } from "./EngDiagQuestionView"
import {
  getEngDiagQuestion,
  getEngDiagSessionState,
  skipEngDiagQuestion,
  submitEngDiagAnswer,
  type EngDiagSessionRef,
} from "../services/eng-diag.service"
import {
  ENG_DIAG_COPY as COPY,
  SHOW_SKILL_PROVENANCE,
} from "../../../constants/diagnostics/eng-diag.constants"
import type {
  EngDiagQuestion,
  EngDiagSessionState,
  EngDiagStudentAnswers,
} from "../types/eng-diag.types"
import styles from "./eng-diag-player.module.css"
import theme from "./eng-diag-theme.module.css"

type Props = {
  /** The lesson activity the student opened. */
  activityKey: string
  /** Called once the last question is answered, so the page can show the finish screen. */
  onFinished?: (state: EngDiagSessionState) => void
}

/**
 * Takes a student through an English diagnostics test, one question at a time.
 *
 * The server decides what comes next: every answer returns the next question's key, and the
 * session state is the source of truth for progress. Nothing about the test is held locally,
 * so closing the tab and coming back resumes exactly where it stopped.
 *
 * The student is never told whether an answer was right. That is deliberate - a diagnostic
 * measures what they can do, and a running score changes how they answer the rest of it.
 */
/**
 * Types with nothing for the student to answer: they read aloud, or just look at something. The
 * tutor marks these afterwards, so the submit guard below must not strand a child on one.
 * Mirrors NON_GRADED_TYPES in tutor-backend modules/eng-diag/answerGrading.js.
 */
const NOTHING_TO_ANSWER = ["read_words", "display_content", "phoneme_segmentation", "flashcard"]

/**
 * Whether there is anything worth submitting.
 *
 * ANY part, not every part. Marking is per part, so a child who can do two of three blanks
 * should be able to hand that in; demanding all three would push them to guess or to skip a
 * question they partly knew. Skipping is the honest way out of one they cannot do, and unlike an
 * empty submission it is not marked wrong.
 */
function hasAnswer(questionType: string | undefined, answers: EngDiagStudentAnswers): boolean {
  if (questionType && NOTHING_TO_ANSWER.includes(questionType)) return true
  return Object.values(answers || {}).some(value => String(value ?? "").trim() !== "")
}

export function EngDiagAttemptPlayer({ activityKey, onFinished }: Props) {
  // Memoised so the callbacks below can depend on it without being rebuilt every render.
  const ref: EngDiagSessionRef = useMemo(() => ({ activity_key: activityKey }), [activityKey])

  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [state, setState] = useState<EngDiagSessionState | null>(null)
  const [question, setQuestion] = useState<EngDiagQuestion | null>(null)
  const [answers, setAnswers] = useState<EngDiagStudentAnswers>({})
  // Named as in BmdTestPlayer, so the two players read the same way.
  const canSubmit = hasAnswer(question?.type, answers)
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)

  /**
   * The part of English this question belongs to, for the pill on the card.
   *
   * Read off the session's own block list rather than off `question.provenance`: provenance is
   * the temporary review aid and goes away with SHOW_SKILL_PROVENANCE, while the block a
   * question was served from is how the test is built and is not going anywhere. It is also
   * the same name the welcome screen already shows the student, so the two agree.
   */
  const cluster = useMemo(() => {
    if (!state?.current_topic_key) return null
    const block = state.topics?.find(
      topic => topic.pk_session_topic_key === state.current_topic_key,
    )
    return block?.cluster_name || null
  }, [state])

  /** When the current question was first shown, for duration_seconds. */
  const shownAt = useRef<number>(Date.now())
  /** Guards against a double click submitting the same answer twice. */
  const inFlight = useRef(false)

  const elapsed = () => Math.max(0, Math.round((Date.now() - shownAt.current) / 1000))

  const showQuestion = useCallback((next: EngDiagQuestion | null) => {
    setQuestion(next)
    setAnswers({})
    // `|| null`, not `?? null`: a timeAllowed of 0 has to mean "no limit". LCMS stores
    // time_allowed as min(0), so 0 is a value an author or a bulk upload can write, and the
    // expiry branch below is `secondsLeft <= 0` - with `??` the question was TIMEOUT-skipped
    // the instant it appeared, twice, and then retired by MAX_SKIPS_PER_QUESTION without the
    // student ever reading it.
    setSecondsLeft(next?.timeAllowed || null)
    shownAt.current = Date.now()
  }, [])

  /** Load the session and whatever question is due. Also how the test resumes. */
  const load = useCallback(async (signal?: AbortSignal) => {
    const session = await getEngDiagSessionState(ref, signal)
    if (session.error) throw new Error(session.error)
    setState(session)

    if (!session.current_question_key) {
      showQuestion(null)
      onFinished?.(session)
      return
    }
    const served = await getEngDiagQuestion(
      { ...ref, session_question_key: session.current_question_key },
      signal,
    )
    if (served.error) throw new Error(served.error)
    showQuestion(served.question)
  }, [ref, showQuestion, onFinished])

  useEffect(() => {
    const controller = new AbortController()
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        await load(controller.signal)
      } catch (e: any) {
        if (!controller.signal.aborted) setError(e?.message || COPY.ERROR_LOAD)
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    })()
    return () => controller.abort()
  }, [load])

  /**
   * Move on after an answer or a skip. The server tells us the next key, so there is no local
   * list to keep in step.
   */
  const advance = useCallback(
    async (result: { session_complete?: boolean; next_question_key?: string | null }) => {
      const session = await getEngDiagSessionState(ref)
      // Both error checks mirror load(). Without them a failed fetch stored the error object as
      // session state and then called showQuestion(undefined), which renders the "all done"
      // card - so a student whose request failed was shown the finish screen, with no error and
      // without onFinished ever firing, and walked away from a test that still owed questions.
      if (session.error) throw new Error(session.error)
      setState(session)

      if (result.session_complete || !result.next_question_key) {
        showQuestion(null)
        onFinished?.(session)
        return
      }
      const served = await getEngDiagQuestion({
        ...ref,
        session_question_key: result.next_question_key,
      })
      if (served.error) throw new Error(served.error)
      showQuestion(served.question)
    },
    [ref, showQuestion, onFinished],
  )

  /**
   * Recover from a failure that happened AFTER the server accepted something.
   *
   * submit() and skip() only reach advance() once the server has returned status 1 - the attempt
   * or skip row is already written. If advance() then fails (the session-state fetch blips), the
   * old question is still on screen with the Submit button live, so a retry writes a SECOND
   * attempt row for the same question; submitAnswer has no idempotency guard, and a second skip
   * counts towards MAX_SKIPS_PER_QUESTION and can retire a question the student answered.
   *
   * Re-loading asks the server what is actually due now, which is the only safe answer after a
   * write we know landed. If that fails too the error already on screen stands.
   */
  const resync = useCallback(async () => {
    try {
      await load()
    } catch {
      // Keep the original error: it describes what the student should act on.
    }
  }, [load])

  const submit = useCallback(async () => {
    if (!question?.sessionQuestionKey || inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    try {
      const result = await submitEngDiagAnswer({
        ...ref,
        session_question_key: question.sessionQuestionKey,
        response: answers,
        duration_seconds: elapsed(),
      })
      if (result.status !== 1) throw new Error(result.error || COPY.ERROR_SUBMIT)
      await advance(result)
    } catch (e: any) {
      setError(e?.message || COPY.ERROR_SUBMIT)
      // The answer was accepted; only moving on failed. Re-sync so the student cannot submit
      // the same question twice.
      await resync()
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }, [ref, question, answers, advance, resync])

  const skip = useCallback(
    async (reason: "MANUAL" | "TIMEOUT") => {
      if (!question?.sessionQuestionKey || inFlight.current) return
      inFlight.current = true
      setBusy(true)
      try {
        const result = await skipEngDiagQuestion({
          ...ref,
          session_question_key: question.sessionQuestionKey,
          skip_reason: reason,
          duration_seconds: elapsed(),
        })
        if (result.status !== 1) throw new Error(result.error || COPY.ERROR_SKIP)
        await advance(result)
      } catch (e: any) {
        setError(e?.message || COPY.ERROR_SKIP)
        // The skip was recorded; a retry would spend another of the question's limited skips.
        await resync()
      } finally {
        inFlight.current = false
        setBusy(false)
      }
    },
    [ref, question, advance, resync],
  )

  /** Per-question countdown, for questions the author gave a time limit. */
  useEffect(() => {
    if (secondsLeft === null || busy) return
    if (secondsLeft <= 0) {
      // Stop the clock BEFORE skipping, not only in showQuestion afterwards. This effect
      // re-runs when `busy` clears, and if skip's advance() failed - a network blip, which is
      // untimed and unretried - secondsLeft would still be 0, so it fired skip again, and
      // again, POSTing a skip row every pass. Clearing it first means a failed timeout stops
      // at one skip and the error is shown; the next question sets its own limit.
      setSecondsLeft(null)
      void skip("TIMEOUT")
      return
    }
    const timer = setTimeout(() => setSecondsLeft(s => (s === null ? null : s - 1)), 1000)
    return () => clearTimeout(timer)
  }, [secondsLeft, busy, skip])

  if (loading) {
    return (
      <div className={`${theme.engDiagTheme} text-center py-5`}>
        <Spinner color="primary" />
        <div className={`mt-3 ${styles.engDiagNoticeBody}`}>{COPY.LOADING}</div>
      </div>
    )
  }

  if (error && !question) {
    return (
      <div className={`${theme.engDiagTheme} ${styles.engDiagNotice}`}>
        <p className={styles.engDiagError}>{error}</p>
      </div>
    )
  }

  if (!question) {
    return (
      <div className={`${theme.engDiagTheme} ${styles.engDiagNotice}`}>
        <div className={styles.engDiagNoticeTitle}>{COPY.FINISH_TITLE}</div>
        <p className={styles.engDiagNoticeBody}>{COPY.FINISH_NOTE}</p>
      </div>
    )
  }

  const answered = state?.answered_questions ?? 0
  const total = state?.total_questions ?? 0
  const percent = total > 0 ? Math.round((answered / total) * 100) : 0

  return (
    <div className={`${theme.engDiagTheme} assessement-section summary-width-hold`}>
      <div className={styles.engDiagCard}>
        {/* ONE header line: strand, what the question is testing, and how far through the
            student is. These were a separate progress card stacked above the question card,
            which cost about 110px of height - enough that a four-picture question pushed its
            last two options below the fold and a child had to scroll to find out they were
            there. Everything here is glanceable rather than read, so it belongs on one line. */}
        <div className={styles.engDiagCardHead}>
          {/* Which part of English this question belongs to. The cluster and nothing finer:
              the welcome screen already lists the clusters by name, so this tells a student
              nothing new, where the topic or the skill would hint at the answer and the YEAR
              is the one thing the player must never reveal. */}
          {cluster ? (
            <span className={styles.engDiagClusterPill}>
              <i className="fas fa-pen-nib" aria-hidden="true" />
              {cluster}
            </span>
          ) : null}

          {/* TEMPORARY review aid - see SHOW_SKILL_PROVENANCE. Shows the curriculum team what
              each question is testing. Off for real students: it names the YEAR, which the
              rest of the player deliberately hides. */}
          {SHOW_SKILL_PROVENANCE && question.provenance ? (
            <div className={styles.engDiagProvenance}>
              <span className="fw-700">{question.provenance.skill}</span>
              <span className="text-muted">
                {[
                  question.provenance.year,
                  question.provenance.cluster,
                  question.provenance.topic,
                  question.provenance.subtopic,
                ]
                  .filter(Boolean)
                  .map(part => ` · ${part}`)
                  .join("")}
              </span>
            </div>
          ) : null}

          <div className={styles.engDiagProgress}>
            <span className={styles.engDiagProgressCount}>
              <span className={styles.engDiagProgressCountValue}>{answered}</span>/{total}
              {/* Progress is by COUNT, never by position in a list: a skipped question comes
                  back, so "question 3 of 10" would go backwards. answered + 1 is "the one you
                  are on", which only ever stands still or goes up. */}
              <span className={styles.engDiagProgressQuestion}>
                {COPY.QUESTION_LABEL_SHORT}
                {Math.min(answered + 1, Math.max(total, 1))}
              </span>
            </span>
            <div
              className={styles.engDiagProgressTrack}
              role="progressbar"
              aria-label={`${answered} of ${total} ${COPY.ANSWERED_SUFFIX}`}
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              {/* A sliver at zero, so the bar reads as a bar before the first answer lands. */}
              <div
                className={styles.engDiagProgressFill}
                style={{ width: `${Math.max(percent, 1)}%` }}
              />
            </div>
            {secondsLeft !== null && (
              <span
                className={`${styles.engDiagTimer} ${
                  secondsLeft <= 10 ? styles.engDiagTimerLow : ""
                }`}
              >
                {secondsLeft}s
              </span>
            )}
          </div>
        </div>

        <EngDiagQuestionView
          question={question}
          value={answers}
          onChange={setAnswers}
        />

        {error && <p className={styles.engDiagError}>{error}</p>}

        <div className={styles.engDiagFooter}>
          <button
            type="button"
            className={styles.engDiagSkip}
            onClick={() => void skip("MANUAL")}
            disabled={busy}
          >
            {COPY.SKIP_FOR_NOW}
            <i className="fas fa-chevron-right" aria-hidden="true" />
          </button>
          {/* An empty submission is marked WRONG, so letting it through costs a child a
              mark for a mis-click. "Skip for now" is the way past a question they cannot do,
              and that is not marked against them. */}
          <div className={styles.engDiagSubmitRow}>
            {!canSubmit && (
              <span className={styles.engDiagSubmitHint}>{COPY.ANSWER_TO_CONTINUE}</span>
            )}
            <button
              type="button"
              className={styles.engDiagSubmit}
              onClick={() => void submit()}
              disabled={busy || !canSubmit}
              title={canSubmit ? undefined : COPY.ANSWER_BEFORE_SUBMIT}
            >
              {busy ? COPY.SUBMITTING : COPY.SUBMIT_ANSWER}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EngDiagAttemptPlayer
